import { GlobalNotificationDrawer } from '@app/Notifications/GlobalNotificationDrawer';
import * as React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  AboutModal,
  Button,
  ButtonVariant,
  Content,
  ContentVariants,
  Dropdown,
  DropdownItem,
  DropdownList,
  Icon,
  Masthead,
  MastheadBrand,
  MastheadContent,
  MastheadLogo,
  MastheadMain,
  MastheadToggle,
  MenuToggle,
  MenuToggleElement,
  Nav,
  NavExpandable,
  NavItem,
  NavList,
  Page,
  PageSidebar,
  PageSidebarBody,
  PageToggleButton,
  SkipToContent,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
} from '@patternfly/react-core';
import { IAppRoute, IAppRouteGroup, routes as staticRoutes } from '@app/routes';
import { CogIcon, PowerOffIcon, QuestionCircleIcon, UserIcon } from '@patternfly/react-icons';
import { useAuth } from 'react-oidc-context';
import { returnToSigninArgs } from '../../utils/returnTo';
import { useApiFetch } from '../../api';
import { GlobalNotificationBadge } from '../Notifications/NotificationBadge';
import { ImpersonationBanner } from '../Impersonation/ImpersonationBanner';
import { NewVersionBanner } from './NewVersionBanner';
import { useNotifications } from '../../contexts/NotificationContext';
import { useUserContext } from '../../contexts/user/UserContext';
import { AncalagonLogo } from '../../assets/icons/Ancalagon-Logo';
import ancalagonLogoPng from '../../assets/icons/Ancalagon-Logo.png';
import aboutModalBgImg from '../../assets/wallpaper_ancalagon_dev-scaled.jpg';

const AppLayout: React.FunctionComponent = () => {
  const { isDrawerOpen, setDrawerOpen } = useNotifications();

  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [isAboutOpen, setIsAboutOpen] = React.useState(false);
  const [routes, setRoutes] = React.useState(staticRoutes);
  const [backendVersion, setBackendVersion] = React.useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const user = useUserContext();
  const isAncalagonStaff = user?.is_ancalagon_staff === true;

  React.useEffect(() => {
    if (!isAboutOpen) return;
    apiFetch('/version')
      .then((res) => res.json())
      .then((data: { status: string; data: { version: string } }) => {
        if (data.status === 'success') {
          setBackendVersion(data.data.version);
        }
      })
      .catch(() => setBackendVersion(null));
  }, [isAboutOpen, apiFetch]);

  const userDropdownItems = [
    <>
      <DropdownItem key="group 2 profile" icon={<UserIcon aria-hidden />}>
        <NavLink to="/profile">My profile</NavLink>
      </DropdownItem>
      <DropdownItem
        key="group 2 logout"
        icon={<PowerOffIcon aria-hidden />}
        onClick={() => void auth.signoutRedirect()}
      >
        Logout
      </DropdownItem>
    </>,
  ];

  const onDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const onDropdownSelect = () => {
    setIsDropdownOpen(false);
  };

  const Header = (
    <Masthead>
      <MastheadMain>
        <MastheadToggle>
          <PageToggleButton id="main-nav-toggle" isHamburgerButton aria-label="Global navigation" />
        </MastheadToggle>
        <MastheadBrand data-codemods>
          <MastheadLogo data-codemods>
            <AncalagonLogo />
          </MastheadLogo>
        </MastheadBrand>
      </MastheadMain>
      <MastheadContent>
        <Toolbar id="toolbar" isFullHeight>
          <ToolbarContent>
            <ToolbarGroup
              align={{ default: 'alignStart' }}
              visibility={{ default: 'hidden', md: 'visible' }}
            >
              <Content component={ContentVariants.h1}>Ancalagon Manager</Content>
            </ToolbarGroup>
            <ToolbarGroup
              /* variant="{ToolbarGroupVariant.default}"*/
              align={{ default: 'alignEnd' }}
            >
              {!auth.isAuthenticated ? (
                <ToolbarItem>
                  <Button
                    icon="Login"
                    aria-label="Notifications"
                    variant={ButtonVariant.plain}
                    onClick={() => {
                      auth.signinRedirect(returnToSigninArgs());
                    }}
                  />
                </ToolbarItem>
              ) : (
                <>
                  <GlobalNotificationBadge />
                  <ToolbarItem>
                    <Button
                      icon={<QuestionCircleIcon />}
                      variant={ButtonVariant.plain}
                      aria-label="About"
                      onClick={() => setIsAboutOpen(true)}
                    />
                  </ToolbarItem>
                  <ToolbarItem>
                    <Dropdown
                      isOpen={isDropdownOpen}
                      onSelect={onDropdownSelect}
                      onOpenChange={(isOpen: boolean) => setIsDropdownOpen(isOpen)}
                      popperProps={{ position: 'right' }}
                      toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                        <MenuToggle
                          ref={toggleRef}
                          icon={
                            <Icon>
                              <CogIcon />
                            </Icon>
                          }
                          onClick={onDropdownToggle}
                          isFullHeight
                          isExpanded={isDropdownOpen}
                        >
                          {auth.user?.profile.given_name}
                        </MenuToggle>
                      )}
                    >
                      <DropdownList>{userDropdownItems}</DropdownList>
                    </Dropdown>
                  </ToolbarItem>
                </>
              )}
            </ToolbarGroup>
          </ToolbarContent>
        </Toolbar>
      </MastheadContent>
    </Masthead>
  );

  const location = useLocation();

  const renderNavItem = (route: IAppRoute, index: number) => (
    <NavItem key={`${route.label}-${index}`} id={`${route.label}-${index}`} isActive={route.path === location.pathname}>
      <NavLink to={route.path}>{route.label}</NavLink>
    </NavItem>
  );

  const renderNavGroup = (group: IAppRouteGroup, groupIndex: number) => (
    <NavExpandable
      key={`${group.label}-${groupIndex}`}
      id={`${group.label}-${groupIndex}`}
      title={group.label}
      isActive={group.routes.some((route) => route.path === location.pathname)}
    >
      {group.routes.map((route, idx) => route.label && renderNavItem(route, idx))}
    </NavExpandable>
  );

  // Admin-only routes (e.g. the "Admin" group) are removed from the nav entirely unless the
  // user is Ancalagon staff. This is the first of two gates — each admin page also guards
  // itself (rendering <NotFound /> for non-staff) so a directly-typed URL is blocked too.
  const visibleRoutes = routes.filter((route) => !route.adminOnly || isAncalagonStaff);

  const Navigation = (
    <Nav id="nav-primary-simple">
      <NavList id="nav-list-simple">
        {visibleRoutes.map(
          (route, idx) => route.label && (!route.routes ? renderNavItem(route, idx) : renderNavGroup(route, idx)),
        )}
      </NavList>
    </Nav>
  );

  const Sidebar = (
    <PageSidebar>
      <PageSidebarBody>{Navigation}</PageSidebarBody>
    </PageSidebar>
  );

  const pageId = 'primary-app-container';

  const PageSkipToContent = (
    <SkipToContent
      onClick={(event) => {
        event.preventDefault();
        const primaryContentContainer = document.getElementById(pageId);
        primaryContentContainer && primaryContentContainer.focus();
      }}
      href={`#${pageId}`}
    >
      Skip to Content
    </SkipToContent>
  );
  return (
    <Page
      mainContainerId={pageId}
      isManagedSidebar
      masthead={Header}
      sidebar={Sidebar}
      // One sticky stack, so the impersonation warning and the new-release notice never overlap.
      banner={
        <div className="ancalagon-page-banners">
          <ImpersonationBanner />
          <NewVersionBanner />
        </div>
      }
      skipToContent={PageSkipToContent}
      notificationDrawer={<GlobalNotificationDrawer />}
      isNotificationDrawerExpanded={isDrawerOpen} // Make sure this prop is being used
      onNotificationDrawerExpand={() => setDrawerOpen(true)} // Add these handlers
    >
      <Outlet />
      <AboutModal
        isOpen={isAboutOpen}
        onClose={() => setIsAboutOpen(false)}
        trademark="Copyright &copy; 2025-2026 Ancalagon - Italique SRL"
        brandImageSrc={ancalagonLogoPng}
        brandImageAlt="Ancalagon Logo"
        productName="Ancalagon Manager"
        backgroundImageSrc={aboutModalBgImg}
        className="ancalagon-about-modal"
      >
        <Content>
          <dl>
            <dt>Frontend Version</dt>
            <dd>{process.env.REACT_APP_VERSION || 'dev'}</dd>
            <dt>Backend Version</dt>
            <dd>{backendVersion || 'loading...'}</dd>
            <dt>User</dt>
            <dd>
              {auth.user?.profile.given_name} {auth.user?.profile.family_name}
            </dd>
            <dt>Environment</dt>
            <dd>{process.env.REACT_APP_NODE_ENV || 'unknown'}</dd>
          </dl>
        </Content>
      </AboutModal>
    </Page>
  );
};

export { AppLayout };
